import { AppHeaderAction } from '@/components/AppHeader/AppHeaderAction';
import { useStoreState } from '@/stores/store';
import { useCallback, useLayoutEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import api from '../../api/axios';
import { useNoInternetDialog } from '@/hooks/useNoInternetDialog';
import { useSentenceSetContext } from '@/hooks/useSentencesetContext';
import { SecondHeader } from './SecondHeader';
import { Icon } from '@/components/Icon';
import { logError } from '@/utils/logHandling';
import { SetSaveModal } from '@/components/SetsSelectedData/SetSaveModal';
import { Modal } from '@/components/ui/therapist/Modal';
import { Button } from '@/components/ui/therapist/Button';
import { Text } from '@/components/Text';
import { fetchUpdatedPatientWithRetry } from '@/utils/fetchUpdatedPatientWithRetry';
import { replacedPatientProfileHeader } from '@/utils/stringHelpers';

const styles = StyleSheet.create({
  header: {
    flexDirection: 'column',
    zIndex: 1,
  },
});

export function Header({ props }) {
  const [isDemoTherapistVisible, setIsDemoTherapistVisible] = useState(false);
  const { isTherapistDemo } = useStoreState((store) => store);
  const { checkInternet } = useNoInternetDialog();

  const {
    apiKeyName,
    setId,
    editData,
    patientId,
    dynamicName,
    selectedData,
    dataKeyName,
  } = useSentenceSetContext();

  const noSentencesSelected = !selectedData.length;

  const [isVisible, setIsVisible] = useState(false);

  const [inputState, setInputState] = useState({
    name: '',
    appDisplayName: '',
  });

  const handleButtonPress = useCallback(() => {
    if (isTherapistDemo) {
      setIsDemoTherapistVisible(true);
    } else {
      setIsVisible((prevState) => {
        if (setId && editData != null) {
          setInputState({
            name: editData.name,
            appDisplayName: editData.appDisplayName,
          });
        }
        return !prevState;
      });
    }
  }, [setId, editData]);

  const handleInputChange = useCallback((key) => {
    return (value) => {
      setInputState((prevState) => {
        return {
          ...prevState,
          [key]: value,
        };
      });
    };
  }, []);

  const handleModalAcceptButton = async () => {
    try {
      await checkInternet();

      if (setId) {
        const state = { ...inputState };
        if (state.name == '') state.name = 'Neues Sätzeset';
        if (state.appDisplayName == '') state.appDisplayName = 'Sätze';

        const bodyData = {
          ...editData,
          [`${dynamicName}Items`]: [],
          ...state,
          updatedAt: new Date().toISOString(),
          patientId: patientId,
          [dataKeyName]: selectedData,
        };
        await api.put(
          `/api/patients/${patientId}/${apiKeyName}/${setId}?api_version=v2`,
          bodyData,
        );

        const { patients } = await fetchUpdatedPatientWithRetry(
          patientId,
          'sentencesets',
          setId,
        );

        props.navigation.navigate('PatientProfile', {
          id: patientId,
          patients,
          replacedTitle: replacedPatientProfileHeader({
            patients,
            patientId,
            patient: undefined,
          }),
        });
      } else {
        const state = { ...inputState };
        if (state.name == '') {
          state.name = 'Neues Sätzeset';
        }
        if (state.appDisplayName == '') {
          state.appDisplayName = 'Sätze';
        }

        const bodyData = {
          ...state,
          patientId: patientId,
          [dataKeyName]: selectedData,
        };
        const { data } = await api.post(
          `/api/patients/${patientId}/${apiKeyName}?api_version=v2`,
          bodyData,
        );

        const { patients } = await fetchUpdatedPatientWithRetry(
          patientId,
          'sentencesets',
          data._id,
        );

        props.navigation.navigate('PatientProfile', {
          id: patientId,
          patients,
          replacedTitle: replacedPatientProfileHeader({
            patients,
            patientId,
            patient: undefined,
          }),
        });
      }
    } catch (error) {
      logError('Error creating sentenceSet', error);
    }
  };

  useLayoutEffect(() => {
    props.navigation.setOptions({
      headerRight: () => (
        <View style={{ gap: 30, flexDirection: 'row' }}>
          <AppHeaderAction
            primary
            onPress={() => props.navigation.goBack()}
            icon={<Icon name="close" scale={1.2} />}
            text="Abbrechen"
          />
          <AppHeaderAction
            primary
            disabled={noSentencesSelected}
            onPress={handleButtonPress}
            icon={<Icon name="save" />}
            text="Speichern"
          />
        </View>
      ),
    });
  }, [noSentencesSelected, handleButtonPress]);

  return (
    <View style={styles.header}>
      <Modal
        title="In der Demo nicht möglich"
        isVisible={isDemoTherapistVisible}
        confirmActionButton={
          <Button
            label="OK"
            look="primary"
            onPress={() => setIsDemoTherapistVisible(false)}
          />
        }
        onClose={() => setIsDemoTherapistVisible(false)}
      >
        <Text style={{ fontSize: 18 }}>
          Registrieren Sie sich bei neolexon, um Ihre eigenen Patient:innen und
          Übungssets zu erstellen.
        </Text>
      </Modal>

      <SecondHeader />
      <SetSaveModal
        isVisible={isVisible}
        setIsVisible={setIsVisible}
        handleInputChange={handleInputChange}
        handleModalAcceptButton={handleModalAcceptButton}
        inputState={inputState}
        handleButtonPress={handleButtonPress}
        modalTitle="Sätzeset speichern"
        apiKeyName={apiKeyName}
      />
    </View>
  );
}
